import axios from 'axios';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { OrderOrShipmentTemplate } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import { useSelectedDomain } from './useSelectedDomain';

export interface UseOrderOrShipmentTemplatesResponse {
  createTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
  defaultOrderTemplate: ComputedRef<OrderOrShipmentTemplate | undefined>;
  defaultShipmentTemplate: ComputedRef<OrderOrShipmentTemplate | undefined>;
  deleteTemplate: (templateId: number) => Promise<void>;
  loadOrderTemplates: () => Promise<void>;
  loadShipmentTemplates: () => Promise<void>;
  orderOrShipmentTemplates: ComputedRef<Loadable<OrderOrShipmentTemplate[]>>;
  updateTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
  setDefaultOrderTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
  setDefaultShipmentTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
  removeDefaultOrderTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
  removeDefaultShipmentTemplate: (
    template: OrderOrShipmentTemplate,
  ) => Promise<OrderOrShipmentTemplate>;
}

const _orderOrShipmentTemplates: Ref<Loadable<OrderOrShipmentTemplate[]>> = ref(
  { loading: false },
);

export function useOrderOrShipmentTemplates(): UseOrderOrShipmentTemplatesResponse {
  const { selectedDomain } = useCurrentUserStore();
  const { t } = useI18n();
  const watchSelectedDomain = useSelectedDomain();
  const toast = useToast();

  const orderOrShipmentTemplates = computed(() => {
    return _orderOrShipmentTemplates.value;
  });

  const defaultOrderTemplate = computed(() => {
    return _orderOrShipmentTemplates.value.data?.find(
      (t) => t.defaultOrderTemplate,
    );
  });

  const defaultShipmentTemplate = computed(() => {
    return _orderOrShipmentTemplates.value.data?.find(
      (t) => t.defaultShipmentTemplate,
    );
  });

  const updateExistingTemplateInPlace = (
    updatedTemplate: OrderOrShipmentTemplate,
  ) => {
    const index =
      _orderOrShipmentTemplates.value.data?.findIndex(
        (s) => s.id === updatedTemplate.id,
      ) ?? -1;
    if (index !== -1) {
      _orderOrShipmentTemplates.value.data?.splice(index, 1, updatedTemplate);
    }
  };

  const createTemplate = async (template: OrderOrShipmentTemplate) => {
    let createdTemplate = template;

    try {
      _orderOrShipmentTemplates.value.loading = true;
      createdTemplate = await api.orderOrShipmentTemplate.create(template);
      _orderOrShipmentTemplates.value.data?.push(createdTemplate);
    } catch (e) {
      if (axios.isCancel(e)) {
        return createdTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error saving order or shipment template', e);
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('templates.templates'),
        }),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return createdTemplate;
  };

  const loadOrderTemplates = async () => {
    try {
      _orderOrShipmentTemplates.value.loading = true;
      _orderOrShipmentTemplates.value.data =
        await api.orderOrShipmentTemplate.findAll();
      watchSelectedDomain(() => loadOrderTemplates());
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error loading order templates', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('templates.templates', 2),
        }),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }
  };

  const loadShipmentTemplates = async () => {
    try {
      _orderOrShipmentTemplates.value.loading = true;
      _orderOrShipmentTemplates.value.data =
        await api.orderOrShipmentTemplate.findAll(true);
      watchSelectedDomain(() => loadShipmentTemplates());
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error loading shipment templates', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('templates.templates', 2),
        }),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }
  };

  const updateTemplate = async (template: OrderOrShipmentTemplate) => {
    let updatedTemplate = template;

    try {
      _orderOrShipmentTemplates.value.loading = true;
      updatedTemplate = await api.orderOrShipmentTemplate.update(template);
      updateExistingTemplateInPlace(updatedTemplate);
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error updating order or shipment template', e);
      toast.addError({
        detail: t('errors.updatingParam', {
          param: t('templates.templates'),
        }),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const deleteTemplate = async (templateId: number) => {
    try {
      _orderOrShipmentTemplates.value.loading = true;
      await api.orderOrShipmentTemplate.delete(templateId);
      _orderOrShipmentTemplates.value.data =
        _orderOrShipmentTemplates.value.data?.filter(
          (s) => s.id !== templateId,
        );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error(
        `Error deleting order or shipment template ${templateId}`,
        e,
      );
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('templates.templates'),
        }),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }
  };

  const setDefaultOrderTemplate = async (
    orderTemplate: OrderOrShipmentTemplate,
  ) => {
    let updatedTemplate = orderTemplate;

    try {
      _orderOrShipmentTemplates.value.loading = true;
      await api.orderOrShipmentTemplate.setDefaultOrderTemplate(
        orderTemplate.id,
      );
      updatedTemplate = new OrderOrShipmentTemplate({
        ...orderTemplate,
        defaultOrderTemplate: true,
      });
      updateExistingTemplateInPlace(updatedTemplate);

      const previousDefault = _orderOrShipmentTemplates.value.data?.find(
        (t) => t.defaultOrderTemplate && t.id !== updatedTemplate.id,
      );
      if (previousDefault) {
        previousDefault.defaultOrderTemplate = false;
      }
      toast.addSuccess({
        detail: t('templates.successSettingDefaultTemplate', {
          templateName: orderTemplate.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error setting default order template', e);
      toast.addError({
        detail: t('templates.errors.settingDefaultTemplate'),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const setDefaultShipmentTemplate = async (
    shipmentTemplate: OrderOrShipmentTemplate,
  ) => {
    let updatedTemplate = shipmentTemplate;

    try {
      _orderOrShipmentTemplates.value.loading = true;
      await api.orderOrShipmentTemplate.setDefaultShipmentTemplate(
        shipmentTemplate.id,
      );
      updatedTemplate = new OrderOrShipmentTemplate({
        ...shipmentTemplate,
        defaultShipmentTemplate: true,
      });
      updateExistingTemplateInPlace(updatedTemplate);

      const previousDefault = _orderOrShipmentTemplates.value.data?.find(
        (t) => t.defaultShipmentTemplate && t.id !== updatedTemplate.id,
      );
      if (previousDefault) {
        previousDefault.defaultShipmentTemplate = false;
      }

      toast.addSuccess({
        detail: t('templates.successSettingDefaultTemplate', {
          templateName: shipmentTemplate.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error setting default shipment template', e);
      toast.addError({
        detail: t('templates.errors.settingDefaultTemplate'),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const removeDefaultOrderTemplate = async (
    orderTemplate: OrderOrShipmentTemplate,
  ) => {
    let updatedTemplate = new OrderOrShipmentTemplate({
      ...orderTemplate,
      defaultOrderTemplate: false,
    });

    try {
      _orderOrShipmentTemplates.value.loading = true;
      updatedTemplate =
        await api.orderOrShipmentTemplate.update(updatedTemplate);
      updateExistingTemplateInPlace(updatedTemplate);
      toast.addSuccess({
        detail: t('templates.successRemovingDefaultTemplate', {
          templateName: orderTemplate.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error removing default order template', e);
      toast.addError({
        detail: t('templates.errors.removingDefaultTemplate'),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const removeDefaultShipmentTemplate = async (
    shipmentTemplate: OrderOrShipmentTemplate,
  ) => {
    let updatedTemplate = new OrderOrShipmentTemplate({
      ...shipmentTemplate,
      defaultShipmentTemplate: false,
    });

    try {
      _orderOrShipmentTemplates.value.loading = true;
      updatedTemplate =
        await api.orderOrShipmentTemplate.update(updatedTemplate);
      updateExistingTemplateInPlace(updatedTemplate);
      toast.addSuccess({
        detail: t('templates.successRemovingDefaultTemplate', {
          templateName: shipmentTemplate.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      _orderOrShipmentTemplates.value.error = e;
      console.error('Error removing default shipment template', e);
      toast.addError({
        detail: t('templates.errors.removingDefaultTemplate'),
        error: e,
      });
    } finally {
      _orderOrShipmentTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  return {
    createTemplate,
    defaultOrderTemplate,
    defaultShipmentTemplate,
    deleteTemplate,
    loadOrderTemplates,
    loadShipmentTemplates,
    orderOrShipmentTemplates,
    removeDefaultOrderTemplate,
    removeDefaultShipmentTemplate,
    setDefaultOrderTemplate,
    setDefaultShipmentTemplate,
    updateTemplate,
  };
}
